import type { RouteParamsGeneric } from 'vue-router'
import { useQuery } from '@tanstack/vue-query'
import { format, isValid, parse } from 'date-fns'

import { DATE_FORMAT_LITE, TIME_SLOTS_CLOCK } from '~/core/constants'
import { locationService, useGolfsService, useProductsService, useRegionsService } from '~/core/services'
import { type DateString, type Hole, LocationType, type Nullable, ResultsCategory, type TimeSlots } from '~/core/types'
import type { SearchGolfsDTO, TimeSlot } from '~/core/types/golfs'
import type { SearchDTO } from '~/core/types/products'
import type { Position } from '~/core/types/shared'

type ResultHole = '6' | '9' | '18'

export interface FiltersParams extends RouteParamsGeneric {
  locationType: LocationType
  productType: ResultsCategory
  locationValue: string
}
type QueryKeys = 'date' | 'promotions' | 'pricing' | 'players' | 'holes' | 'slots'
export function useFilters(search = false, queryKeys: QueryKeys[] = ['date', 'promotions', 'pricing', 'players', 'holes', 'slots']) {
  const route = useRoute()
  const router = useRouter()
  const { geoLocation } = useLocation()
  const { prices: basePrices } = useAppInit()
  const { getRegion } = useRegionsService()
  const { fetchProducts } = useProductsService()
  const { postGolfsSearch, getGolf } = useGolfsService()

  const date = computed(() => 'date' in router.currentRoute.value.query ? router.currentRoute.value.query.date as DateString : format(new Date(), DATE_FORMAT_LITE))
  const pricing = computed(() => 'pricing' in router.currentRoute.value.query ? (router.currentRoute.value.query.pricing as `${number}-${number}`).split('-').map(Number) as [number, number] : null)
  const prices = computed(() => pricing.value ? { min: pricing.value[0], max: pricing.value[1] } : basePrices.value)
  const timeSlots = computed(() => 'slots' in router.currentRoute.value.query ? router.currentRoute.value.query.slots as TimeSlots | TimeSlots[] : null)
  const holes = computed((): Hole[] => {
    if (!('holes' in router.currentRoute.value.query)) {
      return []
    }
    const holes = router.currentRoute.value.query.holes as ResultHole[] | ResultHole
    return Array.isArray(holes) ? holes.map((h) => parseInt(h) as Hole) : [parseInt(holes) as Hole]
  })

  const filters = computed(() => ({
    date: date.value,
    has_promotions: 'promotions' in router.currentRoute.value.query ? router.currentRoute.value.query.promotions === 'true' : false,
    prices: prices.value,
    time_slots: timeSlots.value,
    holes: holes.value,
    players: 'players' in router.currentRoute.value.query ? Number(router.currentRoute.value.query.players) : 1,
    begolf: 'begolf' in router.currentRoute.value.query ? router.currentRoute.value.query.begolf === 'true' : false,
  }))

  const parsedDate = computed(() => {
    const parsed = parse(filters.value.date, DATE_FORMAT_LITE, new Date())
    if (isValid(parsed)) {
      return parsed
    } else {
      router.replace({ ...route, query: { ...route.query, date: format(new Date(), DATE_FORMAT_LITE) } })
      return new Date()
    }
  })

  const timeSlotArray = computed((): TimeSlot[] => {
    if (Array.isArray(filters.value.time_slots)) {
      return filters.value.time_slots.map((ts) => {
        const [start, end] = TIME_SLOTS_CLOCK[ts]
        return {
          start: `${filters.value.date} ${start}`,
          end: `${filters.value.date} ${end}`,
        }
      })
    } else if (filters.value.time_slots) {
      const [start, end] = TIME_SLOTS_CLOCK[filters.value.time_slots]
      return [{
        start: `${filters.value.date} ${start}`,
        end: `${filters.value.date} ${end}`,
      }]
    } else {
      return []
    }
  })

  const productType = computed<ResultsCategory>(() => 'productType' in route.params ? route.params.productType as ResultsCategory : ResultsCategory.GREEN_FEE)
  const locationType = computed<LocationType>(() => 'locationType' in route.params ? route.params.locationType as LocationType : LocationType.COUNTRY)
  const locationValue = computed<string>(() => 'locationValue' in route.params ? route.params.locationValue as LocationType : 'fr')

  const canFetchCity = computed(() => locationType.value === LocationType.CITY)

  const { data: city, isFetching: IsFetchingCity } = useQuery({
    enabled: canFetchCity,
    queryKey: ['search', 'location', locationType, locationValue],
    queryFn: () => locationService.fetchCity(getIdFromSlug(locationValue.value)),
    staleTime: 10 * 1000, // 10s
  })

  const canFetchDepartment = computed(() => locationType.value === LocationType.DEPARTMENT)

  const { data: department, isFetching: IsFetchingDepartment } = useQuery({
    enabled: canFetchDepartment,
    queryKey: ['search', 'location', locationType, locationValue],
    queryFn: () => locationService.fetchDepartment(getIdFromSlug(locationValue.value)),
    staleTime: 10 * 1000, // 10s
  })

  const canFetchRegion = computed(() => locationType.value === LocationType.REGION)

  const { data: region, isFetching: IsFetchingRegion } = useQuery({
    enabled: canFetchRegion,
    queryKey: ['search', 'location', locationType, locationValue],
    queryFn: () => getRegion(getIdFromSlug(locationValue.value)),
    staleTime: 10 * 1000, // 10s
  })

  const canFetchGolf = computed(() => locationType.value === LocationType.GOLF)

  const { data: golf, isFetching: IsFetchingGolf } = useQuery({
    enabled: canFetchGolf,
    queryKey: ['search', 'location', locationType, locationValue],
    queryFn: () => getGolf(getIdFromSlug(locationValue.value)),
    staleTime: 10 * 1000, // 10s
  })

  const isFetchingFiltersData = computed(() => IsFetchingGolf.value || IsFetchingRegion.value || IsFetchingDepartment.value || IsFetchingCity.value)

  const locationLabel = computed(() => {
    switch (locationType.value) {
      case LocationType.CITY:
        return `${city.value?.nom}, ${city.value?.departement.nom}`
      case LocationType.DEPARTMENT:
        return `${department.value?.nom}`
      case LocationType.REGION:
        return `${region.value?.data?.name}`
      case LocationType.GOLF:
        return `${golf.value?.data?.name}`
      case LocationType.COUNTRY:
        return 'Toute la France'
      case LocationType.PROXIMITY:
        return `À proximité`
      default:
        return ''
    }
  })

  const distance = computed(() => {
    switch (locationType.value) {
      case LocationType.PROXIMITY:
        return 150
      case LocationType.CITY:
        return 200
      default:
        return 0
    }
  })

  const position = computed<Nullable<Position>>(() => {
    switch (locationType.value) {
      case LocationType.PROXIMITY:
        return geoLocation.value
      case LocationType.CITY:
        return city.value
          ? {
              latitude: city.value.centre.coordinates[1],
              longitude: city.value.centre.coordinates[0],
            }
          : null
      default:
        return null
    }
  })

  const searchGolfsPayload = computed((): SearchGolfsDTO => ({
    position: position.value ?? undefined, // Voir si on peut faire mieux avec l'api
    distance: distance.value,
    players: filters.value.players,
    services: [],
    time_slots: timeSlotArray.value,
    date: filters.value.date,
    prices: filters.value.prices,
    holes: filters.value.holes,
    has_promotions: filters.value.has_promotions,
    begolf: filters.value.begolf,
    department_id: locationType.value === LocationType.DEPARTMENT && locationValue.value ? parseInt(locationValue.value) : null,
    region_id: locationType.value === LocationType.REGION && locationValue.value ? parseInt(locationValue.value) : null,
  }))

  // Evite un fetch inutile sur le fallback à 0
  const hasMaxValue = computed(() => searchGolfsPayload.value.prices != null && searchGolfsPayload.value.prices.max !== 0)

  const canFetchGolfs = computed(() => productType.value === ResultsCategory.GREEN_FEE
    && search
    && hasMaxValue.value
    && !isFetchingFiltersData.value)

  const { data: golfs, isFetching: isFetchingGolfs } = useQuery({
    enabled: canFetchGolfs,
    queryKey: ['search', 'golfs', searchGolfsPayload],
    queryFn: () => postGolfsSearch(searchGolfsPayload.value),
    staleTime: 10 * 1000, // 10s
  })

  const searchProductsDto = computed<SearchDTO>(() => ({
    position: position.value ?? undefined, // Voir si on peut faire mieux avec l'api
    distance: distance.value,
    category: productType.value,
    department_id: locationType.value === LocationType.DEPARTMENT && locationValue.value ? parseInt(locationValue.value) : null,
    region_id: locationType.value === LocationType.REGION && locationValue.value ? parseInt(locationValue.value) : null,
  }))

  const canFetchProducts = computed(() =>
    productType.value !== ResultsCategory.GREEN_FEE
    && search
    && hasMaxValue.value
    && !isFetchingFiltersData.value)

  const { data: products, isFetching: isFetchingProducts } = useQuery({
    enabled: canFetchProducts,
    queryKey: ['search', 'products', searchProductsDto],
    queryFn: () => fetchProducts(searchProductsDto.value),
    staleTime: 10 * 1000, // 10s
  })

  function updateQuery(f: Partial<typeof filters.value>) {
    const updatedFilters = { ...filters.value, ...f }
    const query = Object.fromEntries(queryKeys.map((k) => {
      switch (k) {
        case 'date':
          return [k, updatedFilters.date ?? date?.value]
        case 'promotions':
          return [k, updatedFilters.has_promotions ? 'true' : 'false']
        case 'pricing':
          return [k, updatedFilters.prices ? `${updatedFilters.prices.min}-${(updatedFilters.prices.max)}` : `${prices.value.min}-${(prices.value.max)}`]
        case 'players':
          return [k, String(updatedFilters.players ?? 1)]
        case 'holes':
          return [k, Array.isArray(updatedFilters.holes) ? updatedFilters.holes.map((h) => `${h}` as ResultHole) : (`${updatedFilters.holes}` as ResultHole)]
        case 'slots':
          return [k, updatedFilters.time_slots ?? timeSlots.value]
      }
    }))
    router.push({ name: route.name, params: { ...route.params }, query: { ...route.query, ...query } })
  }

  function updateParams(p: Partial<FiltersParams>) {
    const params: FiltersParams = {
      locationType: p.locationType ?? locationType.value,
      locationValue: p.locationValue ?? locationValue.value,
      productType: p.productType ?? productType.value,
    }
    router.push({ name: route.name, query: route.query, params })
  }

  const golfIds = computed(() => golfs.value?.data.map(({ id }) => id) ?? [])

  return {
    golfs,
    products,
    golfIds,
    isFetchingProducts,
    isFetchingGolfs,
    filters,
    isFetchingFiltersData,
    locationLabel,
    timeSlotArray,
    updateQuery,
    updateParams,
    productType,
    locationType,
    locationValue,
    position,
    parsedDate,
  }
}
